import {apiRoot} from '../Api';
import imageService from '../services/ImageService';
import {extractErrorMessage, isValidNumber} from '../utils';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import each from 'lodash/each';
import pick from 'lodash/pick';
import debounce from 'lodash/debounce';
import {DEMO_USER_ID, demoImages} from '../demoData';
import {validateFile} from '../validation';
import {addNotification} from '../notifications';
import {addUploadingFilePreview, removeUploadingFilePreview} from '../reducers/uploadingFilePreviews';
import {downloadPoppedImage, printImage} from './EditorCanvasActions';
import {getFilteredAndSortedImages} from '../reducers/imagesSelector';

export const AMPLITUDE_OR_SCALE_CHANGE = 'AMPLITUDE_OR_SCALE_CHANGE';
export const AMPLITUDE_OR_SCALE_PERSIST_SUCCESS = 'AMPLITUDE_OR_SCALE_PERSIST_SUCCESS';
export const AMPLITUDE_OR_SCALE_PERSIST_FAILURE = 'AMPLITUDE_OR_SCALE_PERSIST_FAILURE';

export const onAmplitudeOrScaleChange = (id, {amplitude, spread}, fromPreset = false) => async (dispatch, getState) => {
    const state = getState();
    const {openImage: {userId}, images} = state;
    const res = dispatch({
        type: AMPLITUDE_OR_SCALE_CHANGE,
        id, userId, amplitude, spread, fromPreset
    });
    const imageInfo = images[userId][id];
    if (imageInfo.amplitude === amplitude && imageInfo.spread === spread) {
        // no change, no need to persist
        return res;
    }

    try {
        await imageService.persistSettings(id, amplitude, spread);
        return dispatch({type: AMPLITUDE_OR_SCALE_PERSIST_SUCCESS});
    } catch (error) {
        console.error('error persisting parameters', error);
        return dispatch({AMPLITUDE_OR_SCALE_PERSIST_FAILURE, error, message: 'error persisting amplitude/scale parameters'});
    }
};

export const IMAGE_LOAD_ERROR = 'IMAGE_LOAD_ERROR';

export const onImageLoadError = (url) => {
    return {type: IMAGE_LOAD_ERROR, errorNotification: `Failed to load image from ${url}`};
};

export const FETCH_IMAGES_AND_USER_REQUEST = 'FETCH_IMAGES_AND_USER_REQUEST';
export const FETCH_IMAGES_AND_USER_SUCCESS = 'FETCH_IMAGES_AND_USER_SUCCESS';
export const FETCH_IMAGES_AND_USER_FAILURE = 'FETCH_IMAGES_AND_USER_FAILURE';
export const SWITCH_USER_IMAGES = 'SWITCH_USER_IMAGES';

export const fetchImagesAndUser = (userId) => async (dispatch, getState) => {
    const state = getState();
    const allImages = state.images;
    const imagesFetchStatus = state.imagesFetchStatus;
    const fetchStatus = imagesFetchStatus[userId] || {};
    if (!isEmpty(allImages[userId]) && fetchStatus.fetched) {
        return dispatch({type: SWITCH_USER_IMAGES, user: {id: userId}});
    }
    if (fetchStatus.fetching) {
        return null;
    }
    if (!isValidNumber(userId)) {
        // eslint-disable-next-line no-param-reassign
        userId = state.auth.currentUser.id;
    }
    dispatch({type: FETCH_IMAGES_AND_USER_REQUEST, user: {id: userId}});
    try {
        const {images, user} = await imageService.getImagesAndUser(userId);
        return dispatch({type: FETCH_IMAGES_AND_USER_SUCCESS, images, user});
    } catch (error) {
        console.error(error);
        return dispatch({type: FETCH_IMAGES_AND_USER_FAILURE, userId, error, errorNotification: `Failed to fetch images: ${extractErrorMessage(error)}`});
    }
};

export const IMAGE_FETCH_REQUEST = 'IMAGE_FETCH_REQUEST';
export const IMAGE_FETCH_SUCCESS = 'IMAGE_FETCH_SUCCESS';
export const IMAGE_FETCH_FAILURE = 'IMAGE_FETCH_FAILURE';
export const SWITCH_IMAGE_REQUEST = 'SWITCH_IMAGE_REQUEST';
export const SWITCH_IMAGE_SUCCESS = 'SWITCH_IMAGE_SUCCESS';
export const SWITCH_IMAGE_FAILURE = 'SWITCH_IMAGE_FAILURE';

const fetchImage = (id) => async (dispatch) => {
    dispatch({type: IMAGE_FETCH_REQUEST});

    try {
        const imageInfo = await imageService.getImage(id);
        dispatch({type: IMAGE_FETCH_SUCCESS, imageInfo, userId: imageInfo.userId, id: imageInfo.id});
        return imageInfo;
    } catch (error) {
        dispatch({type: IMAGE_FETCH_FAILURE, error, errorNotification: `Failed to fetch image: ${extractErrorMessage(error)}`});
        throw error;
    }
};

const findImage = (id) => async (dispatch, getState) => {
    const state = getState();
    const allImages = state.images;
    const {openImage: {userId}} = state;
    if (userId) {
        const fetchStatus = state.imagesFetchStatus[userId] || {};
        if (fetchStatus.fetched && !isEmpty(allImages[userId]) && !isEmpty(allImages[userId][id])) {
            return allImages[userId][id];
        }
        if (fetchStatus.fetching) {
            return null;
        }
    }
    const userImages = find(allImages, images => has(images, id));
    if (!isEmpty(userImages)) {
        return userImages[id];
    }
    try {
        return await fetchImage(id)(dispatch);
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const fetchAndSwitchToImage = (id) => async (dispatch, getState) => {
    dispatch({type: SWITCH_IMAGE_REQUEST, id});
    const imageInfo = await findImage(id, true)(dispatch, getState);
    if (imageInfo) {
        // todo: can we let this be async?
        await fetchImagesAndUser(imageInfo.userId)(dispatch, getState);
        dispatch({type: SWITCH_IMAGE_SUCCESS, imageInfo, id});
    } else {
        dispatch({type: SWITCH_IMAGE_FAILURE, id});
    }
};

export const CLOSE_IMAGE = 'CLOSE_IMAGE';

export const onCloseImage = () => {
    return {type: CLOSE_IMAGE};
};

export const POPPING_PROGRESS = 'POPPING_PROGRESS';

export const onPoppingProgress = (uploadId, poppingPct, id, enhId) => {
    return {type: POPPING_PROGRESS, uploadId, poppingPct, id, enhId};
};

export const POPPING_FINISHED = 'POPPING_FINISHED';

export const onPoppingFinished = (uploadId, id, enhId) => (dispatch) => {
    dispatch({type: POPPING_FINISHED, uploadId, id, enhId});
    try {
        return fetchImage(id)(dispatch);
    } catch (e) {
        console.error(e);
    }
};

export const POPPING_ERROR = 'POPPING_ERROR';

export const onPoppingError = (uploadId, error, enhId) => {
    return {type: POPPING_ERROR, uploadId, error, enhId};
};

export const UPLOADS_SELECTED = 'UPLOADS_SELECTED';

export const onUploadsSelected = (rawFiles) => {
    return {type: UPLOADS_SELECTED, rawFiles};
};

export const UPLOAD_STARTED = 'UPLOAD_STARTED';

export const onUploadStarted = (uploadId, response, rawFile) => {
    addUploadingFilePreview(uploadId, rawFile);
    return {type: UPLOAD_STARTED, uploadId};
};

export const UPLOAD_PROGRESS = 'UPLOAD_PROGRESS';

export const onUploadProgress = (uploadId, uploadPct) => {
    return {type: UPLOAD_PROGRESS, uploadId, uploadPct};
};

export const UPLOAD_FINISHED_REQUEST = 'UPLOAD_FINISHED_REQUEST';
export const UPLOAD_FINISHED_SUCCESS = 'UPLOAD_FINISHED_SUCCESS';
export const UPLOAD_FINISHED_FAILURE = 'UPLOAD_FINISHED_FAILURE';

export const onUploadFinished = (uploadId) => async (dispatch) => {
    dispatch({type: UPLOAD_FINISHED_REQUEST});
    try {
        const image = await imageService.getImageByUploadId(uploadId);
        removeUploadingFilePreview(uploadId);
        if (image.error) {
            dispatch(onPoppingError(uploadId, image.error));
        }
        return dispatch({type: UPLOAD_FINISHED_SUCCESS, uploadId, image})
    } catch (error) {
        return dispatch({type: UPLOAD_FINISHED_FAILURE, uploadId, error});
    }
};

export const UPLOAD_ERROR = 'UPLOAD_ERROR';

export const onUploadError = (error, rawFile, uploadId) => {
    return {type: UPLOAD_ERROR, uploadId, error, rawFile};
};

export const DELETE_IMAGE_REQUEST = 'DELETE_IMAGE_REQUEST';
export const DELETE_IMAGE_SUCCESS = 'DELETE_IMAGE_SUCCESS';
export const DELETE_IMAGE_FAILURE = 'DELETE_IMAGE_FAILURE';

export const onDeleteImage = (image) => async (dispatch) => {
    const {id, uploadId, userId} = image;
    dispatch({type: DELETE_IMAGE_REQUEST, image});

    try {
        await imageService.deleteImage(id, uploadId);
        return dispatch({type: DELETE_IMAGE_SUCCESS, id, uploadId, userId});
    } catch (error) {
        return dispatch({type: DELETE_IMAGE_FAILURE, id, uploadId, userId, error});
    }
};

export const SEARCH_TERM_CHANGE_DONE = 'SEARCH_TERM_CHANGE_DONE';

const searchTermChange = (searchTerm, dispatch, getState) => {
    const {sortBy} = getState().myImagesSettings;
    const {userId} = getState().openImage;
    return dispatch({type: SEARCH_TERM_CHANGE_DONE, searchTerm, sortBy, userId});
};

const searchTermChangeDebounced = debounce(searchTermChange, 300);

export const SEARCH_TERM_CHANGE = 'SEARCH_TERM_CHANGE';

export const onSearchInputValueChange = (searchInputValue) => (dispatch, getState) => {
    dispatch({type: SEARCH_TERM_CHANGE, searchInputValue});
    searchTermChangeDebounced(searchInputValue, dispatch, getState);
};

export const onSearchTermChange = (searchTerm) => (dispatch, getState) => {
    searchTermChange(searchTerm, dispatch, getState);
};

export const SORT_BY_CHANGE = 'SORT_BY_CHANGE';

export const onSortByChange = (sortBy) => {
    return {type: SORT_BY_CHANGE, sortBy};
};

export const TOGGLE_SELECT_IMAGE = 'TOGGLE_SELECT_IMAGE';

export const onToggleSelectImage = (id, userId, multi = false, demo = false) => (dispatch, getState) => {
    if (multi) {
        const images = userId === DEMO_USER_ID ? demoImages : getFilteredAndSortedImages(getState());
        return dispatch({type: TOGGLE_SELECT_IMAGE, id, multi, images, demo});
    }
    return dispatch({type: TOGGLE_SELECT_IMAGE, id, demo});
};

export const onToggleSelectImageDemo = (id, userId, multi = false) => {
    return onToggleSelectImage(id, userId, multi, true);
};

export const DELETE_MULTIPLE_IMAGES_REQUEST = 'DELETE_MULTIPLE_IMAGES_REQUEST';
export const DELETE_MULTIPLE_IMAGES_SUCCESS = 'DELETE_MULTIPLE_IMAGES_SUCCESS';
export const DELETE_MULTIPLE_IMAGES_FAILURE = 'DELETE_MULTIPLE_IMAGES_FAILURE';

export const onDeleteSelection = () => async (dispatch, getState) => {
    const {images} = getState().selectedImages;
    const {userId} = getState().openImage;
    dispatch({type: DELETE_MULTIPLE_IMAGES_REQUEST});
    try {
        await imageService.deleteImages(images);
        return dispatch({type: DELETE_MULTIPLE_IMAGES_SUCCESS, ids: images, userId});
    } catch (error) {
        console.error(error);
        return dispatch({type: DELETE_MULTIPLE_IMAGES_FAILURE, ids: images, userId, error});
    }
};

export const onPrintSelection = () => (dispatch, getState) => {
    const state = getState();
    const {images} = state.selectedImages;
    const {userId} = state.openImage;
    const user = state.users[userId];
    const editHistory = state.editHistory;
    const selectedImages = pick(state.images[userId], images);
    each(selectedImages, (imageInfo) => {
        const imageEditHistory = editHistory[imageInfo.id];
        printImage(imageInfo, user, apiRoot, imageEditHistory ? imageEditHistory.edits : null);
    })
};

export const onDownloadSelection = () => (dispatch, getState) => {
    const state = getState();
    const {images} = state.selectedImages;
    const {userId} = state.openImage;
    const user = state.users[userId];
    const editHistory = state.editHistory;
    const selectedImages = pick(state.images[userId], images);
    each(selectedImages, (imageInfo) => {
        const imageEditHistory = editHistory[imageInfo.id];
        downloadPoppedImage(imageInfo, user, apiRoot, imageEditHistory ? imageEditHistory.edits : null);
    })
};

export const RESET_SELECTION = 'RESET_SELECTION';

export const onResetSelection = (demo = false) => (dispatch) => {
    return dispatch({type: RESET_SELECTION, demo});
};

export const onResetSelectionDemo = () => {
    return onResetSelection(true);
};

export const onValidateFile = (rawFile) => {
    const message = validateFile(rawFile);
    if (message) {
        addNotification({message, level: 'error', position: 'bl'});
        return false;
    }
    addNotification({message: 'Uploading Image...', level: 'info', position: 'bl', autoDismiss: 1.5});
    return true;
};

export const PROCESSING_IMAGES_NAV = 'PROCESSING_IMAGES_NAV';

export const onProcessingImagesNav = (idx) => {
    return {type: PROCESSING_IMAGES_NAV, idx};
};

export const PROCESSING_IMAGES_TOGGLE = 'PROCESSING_IMAGES_TOGGLE';

export const onProcessingImagesToggle = () => {
    return {type: PROCESSING_IMAGES_TOGGLE};
};

export const PROCESSING_IMAGES_DRAG = 'PROCESSING_IMAGES_DRAG';

export const onProcessingImagesDrag = (x, y) => {
    return {type: PROCESSING_IMAGES_DRAG, x, y};
};

export const onProcessingImagesDeleteAllFailed = () => (dispatch, getState) => {
    each(getState().processingImages.items, img => {
        if (img.error || (img.status === 'ABORTED') || (img.status === 'ERROR')) {
            onDeleteImage(img)(dispatch);
        }
    });
};

export const SELECTION_NAV_DRAG = 'SELECTION_NAV_DRAG';

export const onSelectionNavDrag = (x, y) => {
    return {type: SELECTION_NAV_DRAG, x, y};
};




// WEBPACK FOOTER //
// ./src/actions/ImageActions.js